import { useEffect, useRef } from 'react';
import { getDbSupabase } from '../lib/supabase';

const DEBOUNCE_MS = 400;

type UseDeliveryArrangementsRealtimeOptions = {
  enabled?: boolean;
  /** Optional filter — e.g. one arrangement id. Omit to listen to the whole table. */
  filter?: string;
  onChange?: () => void;
};

/**
 * Live updates when Lalamove webhook (or any writer) changes delivery_arrangements.
 * Requires the table to be in supabase_realtime publication (migration 134).
 */
export function useDeliveryArrangementsRealtime(
  options: UseDeliveryArrangementsRealtimeOptions = {},
) {
  const { enabled = true, filter, onChange } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        onChangeRef.current?.();
      }, DEBOUNCE_MS);
    };

    const db = getDbSupabase('dem');
    const channelName = filter
      ? `dem-deliveries-live:${filter}`
      : 'dem-deliveries-live';

    let channel = db.channel(channelName).on(
      'postgres_changes',
      {
        event: '*',
        schema: 'data_entry_management',
        table: 'delivery_arrangements',
        ...(filter ? { filter } : {}),
      },
      schedule,
    );

    channel = channel.subscribe();

    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      void db.removeChannel(channel);
    };
  }, [enabled, filter]);
}
